/** @file outlineOf: this frame's outline input (the selected drawable, the hovered joint's drawables). */
import { cssToTarget } from './cssToTarget';
import type { OutlineInput } from './drawableOutline';
import type { PreviewPuppet } from './previewPuppet';
import { quadEditOf } from './quadEditOf';
import type { ViewportCtx } from './viewportCtx';

const NONE: readonly number[] = [];

/** The selection's drawable (by id) and the hovered joint's drawables, as indices in the drawn puppet's `drawables`. */
function outlined(ctx: ViewportCtx, p: PreviewPuppet): { selected: number; hovered: readonly number[] } {
  const drawables = p.bundle.puppet.drawables;
  const id = ctx.store.getState().selection.drawable;
  const node = ctx.hoverNode === null ? undefined : p.names[ctx.hoverNode];
  return {
    selected: id === null ? -1 : drawables.findIndex((d) => d.id === id),
    hovered: node === undefined ? NONE : drawables.flatMap((d, i) => (d.node === node ? [i] : [])),
  };
}

/**
 * Edit mode with a drawn puppet and something to outline (`outlined`: indices match the impostor list's `drawable`
 * slot): the placed impostors, the camera, view and scene units per CSS px; else null.
 */
export function outlineOf(ctx: ViewportCtx): OutlineInput | null {
  const p = ctx.puppet();
  if (ctx.mode !== 'edit' || p === null || ctx.scene === null || ctx.profile === null) return null;
  const { selected, hovered } = outlined(ctx, p);
  if (selected < 0 && hovered.length === 0) return null;
  const view = ctx.scene.camera.view();
  const state = p.instance.state();
  return {
    list: state.list, count: state.count, selected, hovered, edit: quadEditOf(ctx), camera: ctx.scene.camera.contract(), profile: ctx.profile, view,
    px: cssToTarget(ctx) / view.pixelsPerUnit,
  };
}
